import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

declare const turnstile: any;

@Injectable({
  providedIn: 'root'
})
export class TurnstileService {
  private siteKey: string = environment.turnstileSiteKey;
  private isLoaded: boolean = false;
  private loadPromise: Promise<void> | null = null;
  private widgetId: string | null = null;

  constructor() {}

  loadTurnstileScript(): Promise<void> {
    if (this.isLoaded && typeof turnstile !== 'undefined') {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      // Check if already loaded
      if (typeof turnstile !== 'undefined' && turnstile.render) {
        this.isLoaded = true;
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      if (existingScript) {
        // Wait for it to load
        const checkReady = setInterval(() => {
          if (typeof turnstile !== 'undefined' && turnstile.render) {
            clearInterval(checkReady);
            this.isLoaded = true;
            resolve();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        // Wait for turnstile to be ready
        const checkReady = setInterval(() => {
          if (typeof turnstile !== 'undefined' && turnstile.render) {
            clearInterval(checkReady);
            this.isLoaded = true;
            resolve();
          }
        }, 100);
      };

      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Turnstile script'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  renderCaptcha(elementId: string, callback?: (token: string) => void, expiredCallback?: () => void): Promise<string> {
    return this.loadTurnstileScript().then(() => {
      return new Promise<string>((resolve, reject) => {
        try {
          const container = document.getElementById(elementId);
          if (!container) {
            reject(new Error(`Element with id '${elementId}' not found`));
            return;
          }

          // Clear any existing widget in the container
          container.innerHTML = '';

          // Reset widget ID for new render
          this.widgetId = null;

          console.log('Turnstile sitekey being used:', this.siteKey);
          this.widgetId = turnstile.render(container, {
            sitekey: this.siteKey,
            callback: callback,
            'expired-callback': expiredCallback,
            'error-callback': (errorCode: string) => {
              console.error('Turnstile error callback triggered, code:', errorCode);
            }
          });
          resolve(this.widgetId);
        } catch (error) {
          console.error('Error rendering Turnstile:', error);
          reject(error);
        }
      });
    });
  }

  getResponse(): string {
    if (this.widgetId !== null && typeof turnstile !== 'undefined') {
      try {
        return turnstile.getResponse(this.widgetId);
      } catch (e) {
        console.error('Error getting Turnstile response:', e);
        return '';
      }
    }
    return '';
  }

  reset(): void {
    if (this.widgetId !== null && typeof turnstile !== 'undefined') {
      try {
        turnstile.reset(this.widgetId);
      } catch (e) {
        console.error('Error resetting Turnstile:', e);
      }
    }
  }
}
